/**
 * Step 4: Soft reorder home. Fetch /api/ux-layout avec timeout 150ms, cache 60s.
 * Fallback "default" si flags OFF, hors rollout, timeout ou erreur.
 */

import { useState, useEffect, useRef } from "react";
import { getUxFlags } from "@/lib/uxFlags";
import { UX_SESSION_STORAGE_KEY } from "@/lib/uxTracking";

const FETCH_TIMEOUT_MS = 150;
const CACHE_TTL_MS = 60_000;

export type HomeVariant = "trust_first" | "action_first" | "price_first" | "default";

export interface AdaptiveLayoutResult {
  homeVariant: HomeVariant;
  tone?: "soft" | "neutral" | "strong";
  isAdaptive: boolean;
}

interface LayoutCacheEntry {
  data: AdaptiveLayoutResult;
  expiresAt: number;
}

let memoryCache: { sessionId: string; entry: LayoutCacheEntry } | null = null;

function getCached(sessionId: string): AdaptiveLayoutResult | null {
  if (!memoryCache || memoryCache.sessionId !== sessionId || Date.now() >= memoryCache.entry.expiresAt) {
    return null;
  }
  return memoryCache.entry.data;
}

function setCached(sessionId: string, data: AdaptiveLayoutResult): void {
  memoryCache = {
    sessionId,
    entry: { data, expiresAt: Date.now() + CACHE_TTL_MS },
  };
}

const FALLBACK: AdaptiveLayoutResult = {
  homeVariant: "default",
  isAdaptive: false,
};

export function useAdaptiveLayout(): AdaptiveLayoutResult {
  const [result, setResult] = useState<AdaptiveLayoutResult>(FALLBACK);
  const fetched = useRef(false);

  useEffect(() => {
    let cancelled = false;
    const sessionId =
      typeof sessionStorage !== "undefined" ? sessionStorage.getItem(UX_SESSION_STORAGE_KEY) : null;

    const applyFallback = () => {
      if (!cancelled) setResult(FALLBACK);
    };

    const run = async () => {
      const flags = await getUxFlags();
      if (!flags.adaptiveUxEnabled || !flags.uxReorderEnabled || !sessionId) {
        applyFallback();
        return;
      }

      const cached = getCached(sessionId);
      if (cached) {
        if (!cancelled) setResult(cached);
        return;
      }

      const ac = new AbortController();
      const timeoutId = setTimeout(() => ac.abort(), FETCH_TIMEOUT_MS);

      try {
        const r = await fetch(`/api/ux-layout?session_id=${encodeURIComponent(sessionId)}`, {
          credentials: "same-origin",
          signal: ac.signal,
        });
        clearTimeout(timeoutId);
        if (cancelled) return;
        if (r.status !== 200) {
          applyFallback();
          return;
        }
        const data = (await r.json()) as { homeVariant: HomeVariant; tone?: "soft" | "neutral" | "strong" };
        const homeVariant: HomeVariant = data.homeVariant ?? "default";
        const next: AdaptiveLayoutResult = {
          homeVariant,
          tone: data.tone,
          isAdaptive: true,
        };
        setCached(sessionId, next);
        setResult(next);
      } catch {
        if (!cancelled) applyFallback();
      }
    };

    if (!fetched.current) {
      fetched.current = true;
      void run();
    }

    return () => {
      cancelled = true;
    };
  }, []);

  return result;
}
